import { useMemo, useState } from "react";
import PositionModal, { PositionDetails } from "./position-modal";

interface Position {
  id: string;
  title: string;
  year: number;
}
interface Company {
  id: string;
  name: string;
  period: string;
  secondary?: boolean;
  tech: string[];
  positions: Position[];
}

interface MiniCVSectionProps {
  cvData: Company[];

  detailsByPositionId?: Record<string, PositionDetails>;
}

const PositionRow = ({
  title,
  year,
  onClick,
  isClickable,
}: Omit<Position, "id"> & {
  onClick?: () => void;
  isClickable?: boolean;
}) => {
  const Wrapper: any = isClickable ? "button" : "div";

  return (
    <Wrapper
      type={isClickable ? "button" : undefined}
      onClick={onClick}
      className={`content-stretch flex gap-[10px] items-start relative shrink-0 w-full text-left
        ${isClickable ? "group hover:opacity-80 transition-opacity cursor-pointer" : ""}`}
    >
      <div className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative">
        <p
          className={`flex-[1_0_0] font-['Manrope:Regular',sans-serif] font-normal leading-[normal] min-h-px min-w-px relative text-[#334155] text-[12px] whitespace-pre-wrap ${
            isClickable
              ? "decoration-dashed decoration-[#94a3b8] underline-offset-[3px] group-hover:underline"
              : ""
          }`}
        >
          {title}
        </p>
      </div>
      <div className="content-stretch flex flex-[1_0_0] items-center justify-end min-h-px min-w-px relative self-stretch">
        <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[normal] relative shrink-0 text-[#334155] text-[12px] text-right">
          {year}
        </p>
      </div>
    </Wrapper>
  );
};

const CompanyHeader = ({
  name,
  period,
  tech = [],
  secondary = false,
}: Omit<Company, "positions" | "id">) => {
  return (
    <div className="content-stretch flex gap-[10px] items-start relative shrink-0 w-full">
      <div
        className={`content-stretch flex flex-col font-['Manrope:Bold',sans-serif] font-bold items-start leading-[normal] relative shrink-0 ${secondary ? "text-[#777]" : "text-[#0f172a]"}`}
      >
        <p className="relative shrink-0 text-[14px]">{name}</p>
        <p className="relative shrink-0 text-[9px]">{tech.join(", ")}</p>
      </div>
      <div className="content-stretch flex flex-[1_0_0] items-center justify-end min-h-px min-w-px relative self-stretch">
        <p
          className={`font-['Manrope:Regular',sans-serif] font-normal leading-[normal] relative shrink-0 ${secondary ? "text-[#777]" : "text-[#0f172a]"} text-[14px] text-right`}
        >
          {period}
        </p>
      </div>
    </div>
  );
};

const MiniCVSection = ({
  cvData,
  detailsByPositionId = {},
}: MiniCVSectionProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedDetails = useMemo(() => {
    if (!selectedId) return null;
    return detailsByPositionId[selectedId] ?? null;
  }, [selectedId, detailsByPositionId]);

  return (
    <>
      <div className="content-stretch flex flex-col gap-[10px] items-start overflow-clip relative shrink-0 w-full">
        {cvData.map(({ id, name, period, tech, secondary, positions }) => (
          <div
            key={id}
            className="content-stretch flex flex-col items-center justify-center relative shrink-0 w-full gap-[2px]"
          >
            <CompanyHeader
              name={name}
              period={period}
              tech={tech}
              secondary={secondary}
            />
            {positions.map((position) => {
              const isClickable = !!detailsByPositionId[position.id];
              return (
                <PositionRow
                  key={position.id}
                  title={position.title}
                  year={position.year}
                  isClickable={isClickable}
                  onClick={
                    isClickable ? () => setSelectedId(position.id) : undefined
                  }
                />
              );
            })}
          </div>
        ))}
      </div>

      <PositionModal
        open={!!selectedId}
        details={selectedDetails}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};

export default MiniCVSection;
